/**
 * Restoring the open order pane across a page reload (AB#21224).
 *
 * Two mechanisms used to work against each other here, in files three
 * directories apart, and the result was a pane that rendered a header over
 * nothing:
 *
 *  - The deep-link path sets `dashboardRequest.search` to the order number for
 *    one reason only — so the row lands on page one where the restore can find
 *    it. The persist hook then flushed `search` on every hydrate.
 *  - The restore, having missed the row, fetched the order detail anyway and
 *    set `selectedOrderDetails` unconditionally. The header reads that and
 *    renders; the tabs, the action row, Ask ELSA and the timeline read
 *    `selectedRow` / `currentOrderItem`, which stayed null. Nothing reported a
 *    failure, because as far as the code was concerned nothing had failed.
 *
 * Both halves live here now, named, so the next person changing either one can
 * see the other.
 */
import { DashboardItem, OrderDetail, OrderDetailItem } from '@/models';

/** The keys `useDashboard()` persists the open pane under. */
export const OPEN_DETAILS_KEY = 'openDetails';
export const CURRENT_ROW_KEY = 'currentRow';

type StorageLike = Pick<Storage, 'getItem'>;

/**
 * Is a page load resuming an order pane that was open when the user reloaded?
 *
 * Read from storage rather than from the store, because the persist hook that
 * needs this answer runs during hydration, before any composable exists.
 * Unparseable storage answers "no": that lands on the previous behaviour rather
 * than throwing part-way through hydrating the store.
 */
export const hasOrderPaneToRestore = (storage: StorageLike): boolean => {
  try {
    const open = JSON.parse(storage.getItem(OPEN_DETAILS_KEY) ?? 'false');
    const row = JSON.parse(storage.getItem(CURRENT_ROW_KEY) ?? 'null');

    return open === true && row !== null;
  } catch {
    return false;
  }
};

/**
 * The row the detail pane should render, given the page of results and the
 * order that was fetched for the restore.
 *
 * Prefers the real dashboard row — it carries list-only fields (urgency marks,
 * unresolved comment counts) that the detail payload does not. Falls back to
 * the order item, which is not a stand-in: `currentOrderItem` resolves as
 * `selectedOrderDetails.items.find(i => i.id === currentRow.id)`, so the item
 * composed here IS the object the pane goes on to use. Returns null only when
 * the order genuinely has no such item, which means the row is gone.
 */
export const rowForRestoredPane = (
  rowsOnThisPage: DashboardItem[] | undefined | null,
  orderDetail: OrderDetail | null | undefined,
  orderItemId: number | undefined,
): DashboardItem | null => {
  if (orderItemId === undefined || orderItemId === null) {
    return null;
  }

  const fromPage = (rowsOnThisPage ?? []).find((row) => row?.id === orderItemId);
  if (fromPage) {
    return fromPage;
  }

  if (! orderDetail) {
    return null;
  }

  const item = ((orderDetail.items ?? []) as OrderDetailItem[]).find(
    (candidate) => candidate?.id === orderItemId,
  );

  return item ? ({ ...item, order: orderDetail } as unknown as DashboardItem) : null;
};
